'use client';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

export type GatewayName = 'razorpay' | 'phonepe';

export interface GatewayConfig {
  enabled: boolean;
  keyId: string;
  keySecret: string;
  showKeys: boolean;
}

export interface PaymentGatewayState {
  gateways: Record<GatewayName, GatewayConfig>;
  activeGateway: GatewayName | null;
  loading: boolean;
  toggleGateway: (name: GatewayName) => void;
  toggleShowKeys: (name: GatewayName) => void;
  updateKey: (name: GatewayName, field: 'keyId' | 'keySecret', value: string) => void;
  saveGateways: () => Promise<void>;
}

const fallbackGateways: Record<GatewayName, GatewayConfig> = {
  razorpay: {
    enabled: false,
    keyId: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID || '',
    keySecret: '',
    showKeys: false,
  },
  phonepe: {
    enabled: true,
    keyId: process.env.NEXT_PUBLIC_PHONEPE_MERCHANT_ID || '',
    keySecret: '',
    showKeys: false,
  },
};

const PaymentGatewayContext = createContext<PaymentGatewayState | null>(null);

export function PaymentGatewayProvider({ children }: { children: React.ReactNode }) {
  const [gateways, setGateways] = useState<Record<GatewayName, GatewayConfig>>(fallbackGateways);
  const [loading, setLoading] = useState(true);

  const activeGateway: GatewayName | null =
    gateways.phonepe.enabled ? 'phonepe' : gateways.razorpay.enabled ? 'razorpay' : null;

  // Load persisted settings from Supabase on mount
  useEffect(() => {
    const load = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('payment_gateway_settings')
          .select('gateway_name, enabled, key_id, key_secret');

        if (error) throw error;

        if (data && data.length > 0) {
          const updated = { ...fallbackGateways };
          for (const row of data) {
            const name = row.gateway_name as GatewayName;
            if (name === 'phonepe' || name === 'razorpay') {
              updated[name] = {
                enabled: row.enabled,
                keyId: row.key_id || '',
                keySecret: row.key_secret || '',
                showKeys: false,
              };
            }
          }
          setGateways(updated);
        }
      } catch {
        // Silently fall back to defaults if table not yet available
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Persist a single gateway row to Supabase
  const persist = useCallback(async (name: GatewayName, config: GatewayConfig) => {
    try {
      const supabase = createClient();
      await supabase
        .from('payment_gateway_settings')
        .upsert(
          {
            gateway_name: name,
            enabled: config.enabled,
            key_id: config.keyId,
            key_secret: config.keySecret,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'gateway_name' }
        );
    } catch {
      // Non-blocking — UI already reflects the change
    }
  }, []);

  const toggleGateway = useCallback((name: GatewayName) => {
    setGateways((prev) => {
      const newEnabled = !prev[name].enabled;
      const updated = { ...prev };
      updated[name] = { ...prev[name], enabled: newEnabled };
      if (newEnabled) {
        const other = name === 'razorpay' ? 'phonepe' : 'razorpay';
        updated[other] = { ...prev[other], enabled: false };
        // Persist both rows
        persist(other, updated[other]);
      }
      persist(name, updated[name]);
      return updated;
    });
  }, [persist]);

  const toggleShowKeys = useCallback((name: GatewayName) => {
    setGateways((prev) => ({ ...prev, [name]: { ...prev[name], showKeys: !prev[name].showKeys } }));
  }, []);

  const updateKey = useCallback((name: GatewayName, field: 'keyId' | 'keySecret', value: string) => {
    setGateways((prev) => {
      const updated = { ...prev, [name]: { ...prev[name], [field]: value } };
      return updated;
    });
  }, []);

  // Called by the Save button in admin-settings
  const saveGateways = useCallback(async () => {
    const supabase = createClient();
    const rows = (Object.keys(gateways) as GatewayName[]).map((name) => ({
      gateway_name: name,
      enabled: gateways[name].enabled,
      key_id: gateways[name].keyId,
      key_secret: gateways[name].keySecret,
      updated_at: new Date().toISOString(),
    }));
    await supabase
      .from('payment_gateway_settings')
      .upsert(rows, { onConflict: 'gateway_name' });
  }, [gateways]);

  return (
    <PaymentGatewayContext.Provider value={{ gateways, activeGateway, loading, toggleGateway, toggleShowKeys, updateKey, saveGateways }}>
      {children}
    </PaymentGatewayContext.Provider>
  );
}

export function usePaymentGateway() {
  const ctx = useContext(PaymentGatewayContext);
  if (!ctx) throw new Error('usePaymentGateway must be used within PaymentGatewayProvider');
  return ctx;
}
